import type { Application } from 'pixi.js';
import { EDITOR_RENDER_READY_EVENT } from './editorReadiness';

const pending = new WeakMap<Application, number>();
const applications = new Set<Application>();
const frameOwners = new WeakMap<Application, { frames: number; active: number }>();
const frameCounts = new WeakMap<object, { frames: number; active: number }>();
const revealed = new WeakSet<Application>();

/** Completed application canvas renders, not animation callbacks, texture renders,
 * React commits, or physical monitor scanout. Undefined before/after stage life. */
export function readRenderFrameCount(owner: object): number | undefined {
  const count = frameCounts.get(owner);
  return count?.active ? count.frames : undefined;
}

/** Coalesce Pixi mutations into a frame, retaining the editor's idle behavior. */
export function requestRender(app: Application) {
  if (pending.has(app) || !app.stage || app.stage.destroyed) return;
  pending.set(
    app,
    requestAnimationFrame(() => {
      pending.delete(app);
      if (app.stage && !app.stage.destroyed && app.renderer) {
        app.render();
        const count = frameOwners.get(app);
        if (count) count.frames++;
        const canvas = app.canvas;
        if (
          count &&
          canvas instanceof HTMLCanvasElement &&
          canvas.isConnected &&
          !revealed.has(app)
        ) {
          revealed.add(app);
          canvas.setAttribute('data-digitiles-render-ready', '');
          canvas.dispatchEvent(new Event(EDITOR_RENDER_READY_EVENT, { bubbles: true }));
        }
      }
    }),
  );
}
export function registerApplication(app: Application, owner?: object) {
  if (applications.has(app)) return;
  applications.add(app);
  setApplicationRenderOwner(app, owner);
  app.stage.once('destroyed', () => {
    applications.delete(app);
    const count = frameOwners.get(app);
    if (count) count.active--;
    frameOwners.delete(app);
    const frame = pending.get(app);
    if (frame !== undefined) cancelAnimationFrame(frame);
    pending.delete(app);
  });
}
/** A grid view model may change circuits after async application initialization. */
export function setApplicationRenderOwner(app: Application, owner?: object) {
  if (!applications.has(app)) return;
  const previous = frameOwners.get(app);
  let count = owner ? frameCounts.get(owner) : undefined;
  if (owner && !count) {
    count = { frames: 0, active: 0 };
    frameCounts.set(owner, count);
  }
  if (previous === count) return;
  revealed.delete(app);
  if (app.canvas instanceof HTMLCanvasElement)
    app.canvas.removeAttribute('data-digitiles-render-ready');
  if (previous) previous.active--;
  if (count) {
    count.active++;
    frameOwners.set(app, count);
  } else frameOwners.delete(app);
}

export function requestTextureRender() {
  for (const app of applications) requestRender(app);
}
